import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft,
  Download,
  FileSpreadsheet,
  RefreshCw,
  Upload,
  TrendingUp,
  TrendingDown,
  Clock,
  Archive,
  History,
  Trash2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Link2,
  ExternalLink,
  Zap,
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { TickerMappingsManager } from "@/components/TickerMappingsManager";
import { patchEstWorkbook } from "@/lib/patchEstWorkbook";
import { upsertMapping } from "@/lib/tickerMappings";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface StockRow {
  symbol: string;
  isin: string;
}

interface AttemptRecord {
  ticker: string;
  attempt: number;
  startedAt: string;
  durationMs: number;
  ok: boolean;
  status?: number;
  error?: string;
  delayMs?: number;
}

interface QuoteResult {
  symbol: string;
  isin: string;
  success: boolean;
  resolvedTicker?: string;
  name?: string;
  price: number;
  currency: string;
  exchange?: string;
  high52: number;
  low52: number;
  spread52High: number;
  prevClose?: number;
  changePercent?: number;
  priceBasis?: "raw" | "adjusted";
  splitOnPrevDay?: boolean;
  dividendOnPrevDay?: boolean;
  splitRatio?: number | null;
  dividendAmount?: number | null;
  error?: string;
  triedTickers?: string[];
  resolvedVia?: "manual_mapping" | "isin_search" | "symbol_search" | "search" | string;
  isinValid?: boolean;
  // ISIN validation: ticker Yahoo's search returns when querying with the ISIN.
  // If it differs from `resolvedTicker`, we likely fetched a different security.
  isinExpectedTicker?: string | null;
  isinMismatch?: boolean;
  attempts?: AttemptRecord[];
}

// Map a Yahoo Finance ticker (e.g. "NESN.SW", "BMW.DE", "7203.T", "AAPL") to a
// TradingView "EXCHANGE:SYMBOL" URL so users can quickly verify the resolved instrument.
const YAHOO_SUFFIX_TO_TV_EXCHANGE: Record<string, string> = {
  SW: "SIX", VX: "SIX",
  DE: "XETR", F: "FWB", MU: "MUN", BE: "BER", HM: "HAM", HA: "HAN", DU: "DUS", SG: "SWB",
  PA: "EURONEXT", AS: "EURONEXT", BR: "EURONEXT", LS: "EURONEXT",
  L: "LSE", IL: "LSE", IR: "EURONEXT",
  MI: "MIL", MC: "BME", VI: "VIE",
  ST: "OMXSTO", OL: "OSL", CO: "OMXCOP", HE: "OMXHEX", IC: "OMXICE",
  TO: "TSX", V: "TSXV", NE: "NEO", CN: "CSE",
  AX: "ASX", NZ: "NZX",
  T: "TSE", HK: "HKEX", SI: "SGX",
  KS: "KRX", KQ: "KOSDAQ", TW: "TWSE", TWO: "TPEX",
  SS: "SSE", SZ: "SZSE",
  NS: "NSE", BO: "BSE",
  SA: "BMFBOVESPA", MX: "BMV", JO: "JSE", BA: "BCBA", SN: "BCS",
  TA: "TASE", IS: "BIST", AT: "ATHEX", WA: "GPW", PR: "PSE", BD: "BSE",
};

function tradingViewUrl(yahooTicker?: string): string | null {
  const t = (yahooTicker || "").trim().toUpperCase();
  if (!t) return null;
  const dot = t.lastIndexOf(".");
  if (dot === -1) {
    return `https://www.tradingview.com/symbols/${encodeURIComponent(t)}/`;
  }
  const root = t.slice(0, dot);
  const suffix = t.slice(dot + 1);
  const exchange = YAHOO_SUFFIX_TO_TV_EXCHANGE[suffix];
  if (exchange) {
    return `https://www.tradingview.com/symbols/${encodeURIComponent(`${exchange}-${root}`)}/`;
  }
  return `https://www.tradingview.com/symbols/${encodeURIComponent(root)}/`;
}

// Classify a Yahoo edge-function error into a friendly label + suggested next action.
function classifyQuoteError(err: string): {
  label: string;
  tone: "notFound" | "rateLimit" | "server" | "network" | "noPrice" | "unknown";
  suggestion: string;
} {
  const m = err.match(/Yahoo\s+(\d{3})/i);
  if (m) {
    const code = parseInt(m[1], 10);
    if (code === 404) return {
      label: "Ticker not found (404)",
      tone: "notFound",
      suggestion: "Yahoo doesn't recognize any of these symbols. Add a manual mapping with the correct Yahoo ticker.",
    };
    if (code === 429) return {
      label: "Rate-limited by Yahoo (429)",
      tone: "rateLimit",
      suggestion: "Wait a minute, then retry. Reduce frequency of refreshes if it persists.",
    };
    if (code >= 500) return {
      label: `Yahoo server error (${code})`,
      tone: "server",
      suggestion: "Transient — retry in a few seconds. Already auto-retried 3× with backoff.",
    };
    return { label: `Yahoo HTTP ${code}`, tone: "unknown", suggestion: "Retry; if it persists, add a manual mapping." };
  }
  if (/no result|no price|price\s*=?\s*0/i.test(err)) return {
    label: "No price returned",
    tone: "noPrice",
    suggestion: "Symbol resolves but Yahoo returned no price. Try a different exchange suffix via manual mapping.",
  };
  if (/network|fetch failed|timeout|aborted|ECONN|ENOTFOUND|EAI_AGAIN/i.test(err)) return {
    label: "Network / timeout",
    tone: "network",
    suggestion: "Transient connectivity issue — retry.",
  };
  return { label: err || "Unknown error", tone: "unknown", suggestion: "Retry; if it persists, add a manual mapping." };
}

const DEFAULT_REFRESH_INTERVAL_SECONDS = 600; // 10 minutes
const REFRESH_INTERVAL_OPTIONS = [5, 10, 15] as const; // minutes
const REFRESH_INTERVAL_STORAGE_KEY = "spg.refreshIntervalMinutes";
const EST_HISTORY_STORAGE_KEY = "spg.estExportHistory";
const REALTIME_TAB = "realtimedata";
const REALTIME_TAB_ALIASES = ["realtimedata", "realtime"];
const REQUIRED_COLS = ["G", "H", "I", "J"] as const;
const REQUIRED_COL_LABELS: Record<string, string> = { G: "Stock Price", H: "52W High", I: "52W Low", J: "Spread to 52WH (%)" };

const normalizeSheetKey = (value: string) => value.toLowerCase().replace(/\s+/g, "");
const findRealtimeSheetName = (names: string[]) =>
  names.find((n) => REALTIME_TAB_ALIASES.includes(normalizeSheetKey(n))) || null;

const normalizeLookupKey = (value: unknown): string =>
  String(value ?? "").trim().toUpperCase().replace(/\s+/g, " ");

const normalizeCurrencyCode = (value: unknown): string => {
  const raw = normalizeLookupKey(value);
  if (!raw) return "";
  // If the cell looks numeric (e.g. a price slipped into the currency column), reject it.
  if (/^[\d.,\-+\s%']+$/.test(raw)) return "";
  const aliases: Record<string, string> = {
    "$": "USD",
    "US DOLLAR": "USD",
    "US DOLLARS": "USD",
    "U.S. DOLLAR": "USD",
    "U.S. DOLLARS": "USD",
    "€": "EUR",
    "EURO": "EUR",
    "EUROS": "EUR",
    "£": "GBP",
    "POUND": "GBP",
    "POUNDS": "GBP",
    "GBX": "GBp",
    "PENCE": "GBp",
    "¥": "JPY",
    "YEN": "JPY",
    "CHF.": "CHF",
    "SFR": "CHF",
    "FR": "CHF",
  };
  if (aliases[raw]) return aliases[raw];
  // Only accept a clean 3-letter ISO-style currency code; otherwise reject so the
  // UI doesn't surface random text/values from a misaligned column.
  const code = raw.match(/\b[A-Z]{3}\b/);
  return code?.[0] || "";
};

const currencyLookupKeys = (value: unknown): string[] => {
  const raw = normalizeLookupKey(value);
  if (!raw) return [];
  const keys = new Set<string>([raw]);
  const compact = raw.replace(/[\s\-._/]/g, "");
  if (compact.length >= 2) keys.add(compact);
  if (raw.includes(":")) keys.add(raw.split(":").pop() || "");
  const yahooRoot = raw.match(/^([A-Z0-9-]+)\.[A-Z]{1,4}$/);
  if (yahooRoot) keys.add(yahooRoot[1]);
  const bloombergRoot = raw.match(/^([A-Z0-9.-]+)\s+[A-Z]{1,3}$/);
  if (bloombergRoot) keys.add(bloombergRoot[1]);
  return Array.from(keys).filter(Boolean);
};

const addCurrencyLookupKeys = (map: Map<string, string>, value: unknown, currency: string) => {
  currencyLookupKeys(value).forEach((key) => map.set(key, currency));
};

// Tracks WHICH column of the EST realtimedata sheet a currency lookup key came from.
// Used to enrich the currency tooltip with the exact source (Symbol/ISIN/Other column).
export interface EstCurrencySource {
  col: "A" | "B" | "C" | "D" | "E" | "F";
  colName: "Symbol" | "ISIN" | "Other";
  value: string; // raw cell value used to derive the key
}

const COL_INFO: Record<number, { col: "A" | "B" | "C" | "D" | "E" | "F"; colName: "Symbol" | "ISIN" | "Other" }> = {
  0: { col: "A", colName: "Symbol" },
  1: { col: "B", colName: "ISIN" },
  2: { col: "C", colName: "Other" },
  3: { col: "D", colName: "Other" },
  4: { col: "E", colName: "Other" },
  5: { col: "F", colName: "Other" },
};

const addCurrencyLookupSourceKeys = (
  map: Map<string, EstCurrencySource>,
  value: unknown,
  source: EstCurrencySource,
) => {
  currencyLookupKeys(value).forEach((key) => {
    if (!map.has(key)) map.set(key, source);
  });
};

const getCellDisplayValue = (sheet: XLSX.WorkSheet, r: number, c: number) => {
  const cell = sheet[XLSX.utils.encode_cell({ r, c })];
  return cell?.w ?? cell?.v ?? "";
};

const buildEstCurrencyMap = (sheet: XLSX.WorkSheet): Map<string, string> => {
  const map = new Map<string, string>();
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  for (let r = 1; r <= range.e.r; r++) {
    const ccy = normalizeCurrencyCode(getCellDisplayValue(sheet, r, 3));
    if (!ccy || ccy === "CURRENCY") continue;
    for (let c = 0; c <= Math.min(5, range.e.c); c++) {
      addCurrencyLookupKeys(map, getCellDisplayValue(sheet, r, c), ccy);
    }
  }
  return map;
};

const buildEstCurrencySourceMap = (sheet: XLSX.WorkSheet): Map<string, EstCurrencySource> => {
  const map = new Map<string, EstCurrencySource>();
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  for (let r = 1; r <= range.e.r; r++) {
    const ccy = normalizeCurrencyCode(getCellDisplayValue(sheet, r, 3));
    if (!ccy || ccy === "CURRENCY") continue;
    for (let c = 0; c <= Math.min(5, range.e.c); c++) {
      const raw = String(getCellDisplayValue(sheet, r, c) ?? "").trim();
      if (!raw) continue;
      const info = COL_INFO[c];
      addCurrencyLookupSourceKeys(map, raw, { ...info, value: raw });
    }
  }
  return map;
};

const lookupEstCurrency = (map: Map<string, string>, q: QuoteResult): string => {
  for (const value of [q.symbol, q.isin, q.resolvedTicker]) {
    for (const key of currencyLookupKeys(value)) {
      const hit = map.get(key);
      if (hit) return hit;
    }
  }
  return "";
};

// Returns the EST source (column + raw value) that produced the currency match, plus
// which field of the quote (symbol / isin / resolvedTicker) triggered the match.
const lookupEstCurrencySource = (
  map: Map<string, EstCurrencySource>,
  q: QuoteResult,
): (EstCurrencySource & { matchedOn: "symbol" | "isin" | "resolvedTicker"; matchedKey: string }) | null => {
  const fields: Array<{ field: "symbol" | "isin" | "resolvedTicker"; value: unknown }> = [
    { field: "symbol", value: q.symbol },
    { field: "isin", value: q.isin },
    { field: "resolvedTicker", value: q.resolvedTicker },
  ];
  for (const { field, value } of fields) {
    for (const key of currencyLookupKeys(value)) {
      const hit = map.get(key);
      if (hit) return { ...hit, matchedOn: field, matchedKey: key };
    }
  }
  return null;
};

interface EstValidation {
  ok: boolean;
  sheetFound: boolean;
  sheetName: string | null;
  availableSheets: string[];
  columnsPresent: string[]; // e.g. ["G","H"]
  missingColumns: string[]; // e.g. ["I"]
  dataRows: number;
  warnings: string[];
}

function validateEstTemplate(wb: XLSX.WorkBook): EstValidation {
  const availableSheets = wb.SheetNames;
  const sheetName = findRealtimeSheetName(availableSheets);
  if (!sheetName) {
    return {
      ok: false,
      sheetFound: false,
      sheetName: null,
      availableSheets,
      columnsPresent: [],
      missingColumns: [...REQUIRED_COLS],
      dataRows: 0,
      warnings: [`Required tab "${REALTIME_TAB}" / "RealTime" is missing.`],
    };
  }

  const sheet = wb.Sheets[sheetName];
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  const lastCol = range.e.c; // 0-indexed; G=6, H=7, I=8, J=9
  const dataRows = Math.max(0, range.e.r); // rows beyond header

  const columnsPresent: string[] = [];
  const missingColumns: string[] = [];
  const warnings: string[] = [];

  REQUIRED_COLS.forEach((col) => {
    const colIdx = XLSX.utils.decode_col(col);
    if (colIdx <= lastCol) columnsPresent.push(col);
    else missingColumns.push(col);
  });

  if (dataRows === 0) warnings.push(`Tab "${sheetName}" has no data rows below the header.`);
  if (missingColumns.length > 0) {
    warnings.push(
      `Required column${missingColumns.length > 1 ? "s" : ""} ${missingColumns
        .map((c) => `${c} (${REQUIRED_COL_LABELS[c]})`)
        .join(", ")} ${missingColumns.length > 1 ? "are" : "is"} not present in "${sheetName}".`,
    );
  }

  return {
    ok: missingColumns.length === 0 && dataRows > 0,
    sheetFound: true,
    sheetName,
    availableSheets,
    columnsPresent,
    missingColumns,
    dataRows,
    warnings,
  };
}

const csvEscape = (v: unknown): string => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildSnapshotCsv = (results: QuoteResult[], takenAt: Date): string => {
  const iso = takenAt.toISOString();
  const headers = [
    "snapshot_iso",
    "symbol",
    "isin",
    "resolved_ticker",
    "currency",
    "price",
    "prev_close",
    "change_pct",
    "high_52w",
    "low_52w",
    "spread_52w_high_pct",
    "success",
    "error",
  ];
  const lines = [headers.join(",")];
  for (const r of results) {
    lines.push(
      [
        iso,
        r.symbol,
        r.isin,
        r.resolvedTicker || "",
        r.currency || "",
        r.success ? r.price : "",
        r.success && r.prevClose ? r.prevClose : "",
        r.success && typeof r.changePercent === "number" && r.prevClose && r.prevClose > 0
          ? r.changePercent.toFixed(4)
          : "",
        r.success ? r.high52 : "",
        r.success ? r.low52 : "",
        r.success && r.high52 > 0 ? r.spread52High.toFixed(4) : "",
        r.success ? "1" : "0",
        r.error || "",
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return lines.join("\n");
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const tsForFile = (d: Date) => d.toISOString().replace(/[:.]/g, "-").slice(0, 19);

const StockPriceGathering = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [fileListName, setFileListName] = useState<string>("");
  const [estFileName, setEstFileName] = useState<string>("");
  const [estWorkbook, setEstWorkbook] = useState<XLSX.WorkBook | null>(null);
  // Keep the ORIGINAL .xlsx/.xlsm bytes so we can patch them in-place on export.
  // Round-tripping through SheetJS's writer drops charts, images, conditional
  // formatting, pivots, data validations, themes, etc. — patching the ZIP keeps
  // the file byte-identical except for the cells we explicitly overwrite (G–J).
  const estOriginalBytesRef = useRef<ArrayBuffer | null>(null);
  const [estValidation, setEstValidation] = useState<EstValidation | null>(null);
  // Expected currency per stock, parsed from column D of the "realtimedata" tab.
  // Keyed by uppercased symbol AND uppercased ISIN (both point to the same currency)
  // so we can resolve regardless of which one matches.
  const [expectedCurrencyMap, setExpectedCurrencyMap] = useState<Map<string, string>>(new Map());
  const expectedCurrencyMapRef = useRef<Map<string, string>>(new Map());
  // Parallel map: lookup key → which EST column the match came from (Symbol/ISIN/Other).
  const [expectedCurrencySourceMap, setExpectedCurrencySourceMap] = useState<Map<string, EstCurrencySource>>(new Map());
  useEffect(() => {
    expectedCurrencyMapRef.current = expectedCurrencyMap;
  }, [expectedCurrencyMap]);
  // Tracks the last EST currency override pass so the UI can confirm it ran.
  const [estOverrideStatus, setEstOverrideStatus] = useState<{
    at: Date;
    scope: "initial" | "retry" | "single";
    overridden: number;
    total: number;
  } | null>(null);
  // Override Yahoo's currency with the EST realtimedata col D currency (authoritative).
  const applyEstCurrency = (q: QuoteResult): QuoteResult => {
    const map = expectedCurrencyMapRef.current;
    if (!map || map.size === 0) return q;
    const est = lookupEstCurrency(map, q);
    return est && est !== (q.currency || "").toUpperCase() ? { ...q, currency: est } : (est ? { ...q, currency: est } : q);
  };
  const applyEstCurrencyAll = (rs: QuoteResult[], scope: "initial" | "retry" | "single" = "initial"): QuoteResult[] => {
    const map = expectedCurrencyMapRef.current;
    let overridden = 0;
    const out = rs.map((q) => {
      if (!map || map.size === 0) return q;
      const est = lookupEstCurrency(map, q);
      if (est) {
        overridden += 1;
        return { ...q, currency: est };
      }
      return q;
    });
    if (rs.length > 0) {
      setEstOverrideStatus({ at: new Date(), scope, overridden, total: rs.length });
    }
    return out;
  };
  const [stocks, setStocks] = useState<StockRow[]>([]);
  const [quotes, setQuotes] = useState<QuoteResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  // User-configurable auto-refresh interval (persisted for the current dashboard session via sessionStorage).
  const [refreshIntervalMinutes, setRefreshIntervalMinutes] = useState<number>(() => {
    if (typeof window === "undefined") return DEFAULT_REFRESH_INTERVAL_SECONDS / 60;
    const raw = window.sessionStorage.getItem(REFRESH_INTERVAL_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return (REFRESH_INTERVAL_OPTIONS as readonly number[]).includes(parsed)
      ? parsed
      : DEFAULT_REFRESH_INTERVAL_SECONDS / 60;
  });
  const refreshIntervalSeconds = refreshIntervalMinutes * 60;
  const [secondsUntilNext, setSecondsUntilNext] = useState(refreshIntervalSeconds);
  const [autoRefresh, setAutoRefresh] = useState(false);
  // Auto-export the EST workbook with refreshed quotes after every refresh cycle.
  const [autoExportEst, setAutoExportEst] = useState(false);
  const lastAutoExportRefresh = useRef<number | null>(null);
  const [autoExportStatus, setAutoExportStatus] = useState<{
    ranAt: Date;
    fileName: string;
    writtenRows: number;
    fullyPopulatedRows: number;
    partialRows: { row: number; symbol: string; missing: string[] }[];
    columnsWritten: { G: number; H: number; I: number; J: number; W: number };
  } | null>(null);

  // Last 5 generated EST_updated_<ts>.xlsx files held in-memory for re-download.
  // Metadata persists in sessionStorage; the actual Blob lives only for the current session
  // (Blobs cannot be JSON-serialized), so entries restored from storage are re-download disabled.
  const [estExportHistory, setEstExportHistory] = useState<
    { fileName: string; createdAt: Date; size: number; blob?: Blob; rows: number }[]
  >(() => {
    try {
      const raw = window.sessionStorage.getItem(EST_HISTORY_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((e) => e && typeof e.fileName === "string")
        .slice(0, 5)
        .map((e) => ({
          fileName: String(e.fileName),
          createdAt: new Date(e.createdAt),
          size: Number(e.size) || 0,
          rows: Number(e.rows) || 0,
        }));
    } catch {
      return [];
    }
  });
  const [estHistoryFilter, setEstHistoryFilter] = useState("");

  // Persist metadata (without blob) to sessionStorage whenever history changes.
  useEffect(() => {
    try {
      const serializable = estExportHistory.map((f) => ({
        fileName: f.fileName,
        createdAt: f.createdAt.toISOString(),
        size: f.size,
        rows: f.rows,
      }));
      window.sessionStorage.setItem(EST_HISTORY_STORAGE_KEY, JSON.stringify(serializable));
    } catch {
      /* ignore quota errors */
    }
  }, [estExportHistory]);

  const [snapshots, setSnapshots] = useState<{ takenAt: Date; csv: string; rows: number; ok: number }[]>([]);
  const [exportReport, setExportReport] = useState<{
    exportedAt: Date;
    sheetName: string;
    fileName: string;
    sheetRows: number;
    matched: { symbol: string; isin: string; row: number; matchedOn: string; populated: ("G" | "H" | "I" | "J" | "W")[]; missing: string[]; price?: number; prevClose?: number; changePercent?: number; priceBasis?: "raw" | "adjusted"; currency?: string }[];
    quotesNotInSheet: { symbol: string; isin: string; reason: string }[];
    sheetRowsNotMatched: { row: number; values: string[] }[];
    columnsWritten: { G: number; H: number; I: number; J: number; W: number };
  } | null>(null);
  const [matchedSortDir, setMatchedSortDir] = useState<"none" | "asc" | "desc">("none");
  const [retryReport, setRetryReport] = useState<{
    retriedAt: Date;
    attempted: number;
    invokeError?: string | null;
    recovered: { symbol: string; isin: string; price: number; rawResponse: QuoteResult }[];
    stillFailing: { symbol: string; isin: string; previousError: string; currentError: string; rawResponse: QuoteResult }[];
    missingFromResponse: { symbol: string; isin: string; previousError: string; rawResponse: null }[];
  } | null>(null);
  const [retryReportOpen, setRetryReportOpen] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [mappingPrefill, setMappingPrefill] = useState<{ symbol?: string; isin?: string; resolved?: string; notes?: string } | null>(null);
  const [diagnosisFilter, setDiagnosisFilter] = useState<"all" | "notFound" | "rateLimit" | "network" | "noPrice" | "server" | "unknown">("all");
  const [onlyEstCurrency, setOnlyEstCurrency] = useState(false);
  const [deltaPosThreshold, setDeltaPosThreshold] = useState<number>(() => {
    const v = parseFloat(localStorage.getItem("spg.deltaPosThreshold") || "");
    return Number.isFinite(v) ? v : 2;
  });
  const [deltaNegThreshold, setDeltaNegThreshold] = useState<number>(() => {
    const v = parseFloat(localStorage.getItem("spg.deltaNegThreshold") || "");
    return Number.isFinite(v) ? v : -2;
  });
  useEffect(() => {
    localStorage.setItem("spg.deltaPosThreshold", String(deltaPosThreshold));
  }, [deltaPosThreshold]);
  useEffect(() => {
    localStorage.setItem("spg.deltaNegThreshold", String(deltaNegThreshold));
  }, [deltaNegThreshold]);
  const [priceBasis, setPriceBasis] = useState<"raw" | "adjusted">(() => {
    const v = localStorage.getItem("spg.priceBasis");
    return v === "adjusted" ? "adjusted" : "raw";
  });
  useEffect(() => {
    localStorage.setItem("spg.priceBasis", priceBasis);
  }, [priceBasis]);

  const refreshTimer = useRef<number | null>(null);
  const countdownTimer = useRef<number | null>(null);
  const retryInFlight = useRef(false);
  const fetchInFlight = useRef(false);
  const hydratedRef = useRef(false);

  // Hydrate persisted quotes / stocks / lastRefresh / retryReport on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem("stockPriceGathering.state.v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed.stocks)) setStocks(parsed.stocks);
        if (Array.isArray(parsed.quotes)) setQuotes(parsed.quotes);
        if (typeof parsed.lastRefresh === "string") {
          const d = new Date(parsed.lastRefresh);
          if (!isNaN(d.getTime())) setLastRefresh(d);
        }
        // Rehydrate the most-recent Retry Report so the diagnosis (404 / 429 / network /
        // no-price) and the per-symbol triedTickers list survive a page refresh.
        if (parsed.retryReport && typeof parsed.retryReport === "object") {
          const rr = parsed.retryReport;
          const ts = new Date(rr.retriedAt);
          setRetryReport({
            attempted: Number(rr.attempted) || 0,
            retriedAt: isNaN(ts.getTime()) ? new Date() : ts,
            invokeError: rr.invokeError ?? null,
            recovered: Array.isArray(rr.recovered) ? rr.recovered : [],
            stillFailing: Array.isArray(rr.stillFailing) ? rr.stillFailing : [],
            missingFromResponse: Array.isArray(rr.missingFromResponse) ? rr.missingFromResponse : [],
          });
        }
      }
    } catch {
      // ignore corrupted persisted state
    } finally {
      hydratedRef.current = true;
    }
  }, []);

  // Persist quotes / stocks / lastRefresh / retryReport so failed retries + diagnoses
  // survive reloads.
  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      localStorage.setItem(
        "stockPriceGathering.state.v1",
        JSON.stringify({
          stocks,
          quotes,
          lastRefresh: lastRefresh ? lastRefresh.toISOString() : null,
          retryReport: retryReport
            ? {
                attempted: retryReport.attempted,
                retriedAt: retryReport.retriedAt.toISOString(),
                invokeError: retryReport.invokeError ?? null,
                recovered: retryReport.recovered,
                stillFailing: retryReport.stillFailing,
                missingFromResponse: retryReport.missingFromResponse,
              }
            : null,
        }),
      );
    } catch {
      // localStorage may be full or unavailable
    }
  }, [stocks, quotes, lastRefresh, retryReport]);

  // Parse FileList xlsx (col A symbol, col B ISIN)
  const handleFileList = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "" });

      const parsed: StockRow[] = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const a = String(row[0] ?? "").trim();
        const b = String(row[1] ?? "").trim();
        if (!a) continue;
        // Skip a header row
        if (i === 0 && /symbol|ticker/i.test(a)) continue;
        parsed.push({ symbol: a.toUpperCase(), isin: b.toUpperCase() });
      }

      // De-duplicate on (symbol|isin) pairs, preserving first occurrence
      const seen = new Set<string>();
      const deduped: StockRow[] = [];
      for (const s of parsed) {
        const key = `${s.symbol}|${s.isin}`;
        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(s);
      }
      const dupesRemoved = parsed.length - deduped.length;

      setFileListName(file.name);
      setStocks(deduped);
      setQuotes([]);
      toast({
        title: "FileList loaded",
        description:
          dupesRemoved > 0
            ? `${deduped.length} unique stocks parsed (${dupesRemoved} duplicate${dupesRemoved === 1 ? "" : "s"} removed).`
            : `${deduped.length} stocks parsed.`,
      });
    } catch (e) {
      toast({
        title: "Failed to parse FileList",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  // Load EST workbook
  const handleEstFile = async (file: File) => {
    try {
      const buf = await file.arrayBuffer();
      // Preserve EVERYTHING from the user's original workbook so the export is byte-equivalent
      // in design, formulas, formats, and (for .xlsm) macros — we only overwrite cells G–J.
      const wb = XLSX.read(buf, {
        type: "array",
        cellStyles: true,   // keep cell formatting
        cellFormula: true,  // keep formulas (don't replace with cached values)
        cellNF: true,       // keep number formats
        cellDates: true,    // preserve date typing
        sheetStubs: true,   // keep empty cells
        bookVBA: true,      // preserve VBA macros for .xlsm
      });
      const validation = validateEstTemplate(wb);
      setEstFileName(file.name);
      setEstWorkbook(wb);
      // Keep an immutable copy of the original bytes for in-place patching on export.
      estOriginalBytesRef.current = buf.slice(0);
      setEstValidation(validation);

      if (validation.ok) {
        // Build expected-currency lookup from column D of the realtimedata tab.
        const currencyMap = buildEstCurrencyMap(wb.Sheets[validation.sheetName!]);
        const sourceMap = buildEstCurrencySourceMap(wb.Sheets[validation.sheetName!]);
        try {
          setQuotes((prev) => prev.map((q) => {
            const est = lookupEstCurrency(currencyMap, q);
            return est ? { ...q, currency: est } : q;
          }));
        } catch (err) {
          console.warn("Failed to apply currency column D from realtimedata", err);
        }
        setExpectedCurrencyMap(currencyMap);
        setExpectedCurrencySourceMap(sourceMap);
        toast({
          title: "EST workbook validated",
          description: `Tab "${validation.sheetName}" found · ${validation.dataRows} rows · ${currencyMap.size > 0 ? `${currencyMap.size} currency entries loaded from column D` : "no currencies parsed from column D"}.`,
        });
      } else {
        setExpectedCurrencyMap(new Map());
        setExpectedCurrencySourceMap(new Map());
        toast({
          title: "Template validation failed",
          description: validation.warnings.join(" "),
          variant: "destructive",
        });
      }
    } catch (e) {
      setEstValidation(null);
      toast({
        title: "Failed to load EST workbook",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const fetchQuotes = async () => {
    if (stocks.length === 0) return;
    if (fetchInFlight.current || retryInFlight.current) return; // prevent overlapping fetches
    fetchInFlight.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("fetch-yahoo-quote", {
        body: { stocks: stocks.map((s) => ({ symbol: s.symbol, isin: s.isin })), priceBasis },
      });
      if (error) throw error;
      const results: QuoteResult[] = applyEstCurrencyAll(data?.results || []);
      setQuotes(results);
      const takenAt = new Date();
      setLastRefresh(takenAt);
      setSecondsUntilNext(refreshIntervalSeconds);
      const ok = results.filter((r) => r.success).length;

      // Capture CSV snapshot for historical tracking
      const csv = buildSnapshotCsv(results, takenAt);
      setSnapshots((prev) => [{ takenAt, csv, rows: results.length, ok }, ...prev].slice(0, 144)); // ~24h at 10min cadence

      toast({
        title: "Quotes refreshed",
        description: `${ok}/${results.length} stocks fetched · snapshot saved.`,
      });
    } catch (e) {
      toast({
        title: "Refresh failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      fetchInFlight.current = false;
    }
  };

  const retryFailedQuotes = async () => {
    if (retryInFlight.current || fetchInFlight.current) {
      return; // Ignore rapid repeated clicks while a retry/fetch is already running
    }
    const failed = quotes.filter((q) => !q.success);
    if (failed.length === 0) {
      toast({ title: "Nothing to retry", description: "All quotes succeeded in the last refresh." });
      return;
    }
    retryInFlight.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("fetch-yahoo-quote", {
        body: { stocks: failed.map((q) => ({ symbol: q.symbol, isin: q.isin })), priceBasis },
      });
      if (error) throw error;
      const retried: QuoteResult[] = applyEstCurrencyAll(data?.results || [], "retry");
      const retryMap = new Map(retried.map((r) => [`${r.symbol}|${r.isin}`, r]));

      // Re-apply override across the merged set so any rows whose currency was previously
      // unset (or stale) are now consistent with the EST authoritative source.
      const mergedRaw = quotes.map((q) => {
        const key = `${q.symbol}|${q.isin}`;
        return retryMap.has(key) ? (retryMap.get(key) as QuoteResult) : q;
      });
      const merged = applyEstCurrencyAll(mergedRaw, "retry");
      setQuotes(merged);

      const recovered = retried.filter((r) => r.success).length;
      const stillFailing = retried.length - recovered;

      // Build per-symbol retry report
      const recoveredList: { symbol: string; isin: string; price: number; rawResponse: QuoteResult }[] = [];
      const stillFailingList: { symbol: string; isin: string; previousError: string; currentError: string; rawResponse: QuoteResult }[] = [];
      const missingFromResponse: { symbol: string; isin: string; previousError: string; rawResponse: null }[] = [];
      for (const f of failed) {
        const key = `${f.symbol}|${f.isin}`;
        const r = retryMap.get(key);
        if (!r) {
          missingFromResponse.push({ symbol: f.symbol, isin: f.isin, previousError: f.error || "unknown", rawResponse: null });
        } else if (r.success) {
          recoveredList.push({ symbol: r.symbol, isin: r.isin, price: r.price, rawResponse: r });
        } else {
          stillFailingList.push({
            symbol: r.symbol,
            isin: r.isin,
            previousError: f.error || "unknown",
            currentError: r.error || "unknown",
            rawResponse: r,
          });
        }
      }
      setExpandedRows({});
      setRetryReport({
        retriedAt: new Date(),
        attempted: failed.length,
        recovered: recoveredList,
        stillFailing: stillFailingList,
        missingFromResponse,
      });
      if (stillFailingList.length > 0 || missingFromResponse.length > 0) {
        setRetryReportOpen(true);
      }

      // Snapshot the merged state so history reflects the retry
      const takenAt = new Date();
      const ok = merged.filter((q) => q.success).length;
      const csv = buildSnapshotCsv(merged, takenAt);
      setSnapshots((prev) => [{ takenAt, csv, rows: merged.length, ok }, ...prev].slice(0, 144));

      toast({
        title: "Retry complete",
        description: `${recovered}/${retried.length} recovered${stillFailing > 0 ? ` · ${stillFailing} still failing` : ""}.`,
        variant: stillFailing > 0 && recovered === 0 ? "destructive" : undefined,
      });
    } catch (e) {
      toast({
        title: "Retry failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      retryInFlight.current = false;
    }
  };

  // Save mapping then immediately re-fetch the single failing row.
  const [applyingRow, setApplyingRow] = useState<string | null>(null);
  const applyMappingAndRefetch = async (
    row: { symbol: string; isin: string },
    resolved: string,
    note: string,
  ) => {
    const key = `${row.symbol}|${row.isin}`;
    setApplyingRow(key);
    try {
      // 1. Persist the mapping
      await upsertMapping({
        input_symbol: row.symbol || null,
        input_isin: row.isin || null,
        resolved_symbol: resolved,
        notes: note,
      });

      // 2. Re-run the quote for just this row
      const { data, error } = await supabase.functions.invoke("fetch-yahoo-quote", {
        body: { stocks: [{ symbol: row.symbol, isin: row.isin }], priceBasis },
      });
      if (error) throw error;
      const rawResult: QuoteResult | undefined = data?.results?.[0];
      if (!rawResult) throw new Error("No quote returned");
      const [result] = applyEstCurrencyAll([rawResult], "single");

      // 3. Patch quotes state
      setQuotes((prev) => prev.map((q) => (`${q.symbol}|${q.isin}` === key ? result : q)));

      // 4. Patch the open retry report (move row between buckets)
      setRetryReport((prev) => {
        if (!prev) return prev;
        const stillFailing = prev.stillFailing.filter((r) => `${r.symbol}|${r.isin}` !== key);
        const recovered = [...prev.recovered];
        if (result.success) {
          recovered.unshift({ symbol: result.symbol, isin: result.isin, price: result.price, rawResponse: result });
        } else {
          stillFailing.unshift({
            symbol: result.symbol,
            isin: result.isin,
            previousError: prev.stillFailing.find((r) => `${r.symbol}|${r.isin}` === key)?.currentError || "unknown",
            currentError: result.error || "unknown",
            rawResponse: result,
          });
        }
        return { ...prev, recovered, stillFailing };
      });

      toast({
        title: result.success ? "Mapping applied · quote recovered" : "Mapping applied · still failing",
        description: result.success
          ? `${row.symbol || row.isin} → ${resolved} = ${result.price} ${result.currency}`
          : `${resolved}: ${result.error || "unknown error"}`,
        variant: result.success ? "default" : "destructive",
      });
    } catch (e) {
      toast({
        title: "Apply & refetch failed",
        description: e instanceof Error ? e.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setApplyingRow(null);
    }
  };

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) {
      if (refreshTimer.current) window.clearInterval(refreshTimer.current);
      if (countdownTimer.current) window.clearInterval(countdownTimer.current);
      return;
    }
    refreshTimer.current = window.setInterval(() => {
      fetchQuotes();
    }, refreshIntervalSeconds * 1000);
    countdownTimer.current = window.setInterval(() => {
      setSecondsUntilNext((s) => (s > 0 ? s - 1 : refreshIntervalSeconds));
    }, 1000);
    return () => {
      if (refreshTimer.current) window.clearInterval(refreshTimer.current);
      if (countdownTimer.current) window.clearInterval(countdownTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, stocks, refreshIntervalSeconds]);

  // Auto-export to EST after each refresh (browser timer driven).
  // Triggers whenever lastRefresh advances AND auto-export is enabled AND a workbook is loaded.
  const exportToEstRef = useRef<(() => void | Promise<void>) | null>(null);
  useEffect(() => {
    if (!autoExportEst) return;
    if (!lastRefresh) return;
    const ts = lastRefresh.getTime();
    if (lastAutoExportRefresh.current === ts) return;
    if (!estWorkbook || quotes.length === 0) return;
    if (estValidation && !estValidation.ok) return;
    lastAutoExportRefresh.current = ts;
    exportToEstRef.current?.();
    // exportToEst updates exportReport synchronously; mirror it into the auto-export status label.
    setTimeout(() => {
      setExportReport((rep) => {
        if (rep) {
          const partialRows = rep.matched
            .filter((m) => m.missing.length > 0)
            .map((m) => ({ row: m.row, symbol: m.symbol, missing: m.missing }));
          setAutoExportStatus({
            ranAt: rep.exportedAt,
            fileName: rep.fileName,
            writtenRows: rep.matched.length,
            fullyPopulatedRows: rep.matched.length - partialRows.length,
            partialRows,
            columnsWritten: rep.columnsWritten,
          });
        }
        return rep;
      });
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastRefresh, autoExportEst, estWorkbook, quotes.length, estValidation]);

  const downloadSnapshot = (idx: number) => {
    const snap = snapshots[idx];
    if (!snap) return;
    downloadBlob(new Blob([snap.csv], { type: "text/csv;charset=utf-8" }), `snapshot_${tsForFile(snap.takenAt)}.csv`);
  };

  const downloadAllSnapshotsZip = async () => {
    if (snapshots.length === 0) return;
    const zip = new JSZip();
    for (const snap of snapshots) {
      zip.file(`snapshot_${tsForFile(snap.takenAt)}.csv`, snap.csv);
    }
    // Combined long-format CSV (all snapshots concatenated, single header)
    const combined: string[] = [];
    snapshots.forEach((snap, i) => {
      const lines = snap.csv.split("\n");
      if (i === 0) combined.push(lines[0]);
      combined.push(...lines.slice(1));
    });
    zip.file("all_snapshots_combined.csv", combined.join("\n"));
    const blob = await zip.generateAsync({ type: "blob" });
    downloadBlob(blob, `stock-snapshots_${tsForFile(new Date())}.zip`);
  };

  const clearSnapshots = () => {
    setSnapshots([]);
    toast({ title: "Snapshots cleared" });
  };

  const exportToEst = async () => {
    if (!estWorkbook || !estOriginalBytesRef.current) {
      toast({ title: "No EST workbook loaded", variant: "destructive" });
      return;
    }
    if (quotes.length === 0) {
      toast({ title: "No quotes to export", description: "Refresh quotes first.", variant: "destructive" });
      return;
    }
    // Re-validate template right before writing (defensive)
    const validation = estValidation ?? validateEstTemplate(estWorkbook);
    if (!validation.ok) {
      setEstValidation(validation);
      toast({
        title: "Cannot export — template invalid",
        description: validation.warnings.join(" "),
        variant: "destructive",
      });
      return;
    }

    const wb = estWorkbook;
    const sheetName = findRealtimeSheetName(wb.SheetNames);
    if (!sheetName) {
      toast({ title: `"${REALTIME_TAB}" tab not found`, variant: "destructive" });
      return;
    }
    const sheet = wb.Sheets[sheetName];

    // Read existing rows; assume row 1 is header. Match by symbol in col A or B.
    const aoa = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: "" });
    const quoteMap = new Map<string, QuoteResult>();
    quotes.forEach((q) => {
      currencyLookupKeys(q.symbol).forEach((key) => quoteMap.set(key, q));
      currencyLookupKeys(q.isin).forEach((key) => quoteMap.set(key, q));
      currencyLookupKeys(q.resolvedTicker).forEach((key) => quoteMap.set(key, q));
    });

    const matched: { symbol: string; isin: string; row: number; matchedOn: string; populated: ("G" | "H" | "I" | "J" | "W")[]; missing: string[]; price?: number; prevClose?: number; changePercent?: number; priceBasis?: "raw" | "adjusted"; currency?: string }[] = [];
    const sheetRowsNotMatched: { row: number; values: string[] }[] = [];
    const usedQuoteKeys = new Set<string>();
    const columnsWritten = { G: 0, H: 0, I: 0, J: 0, W: 0 };
    // Pending in-place patches for the realtimedata sheet — applied to the
    // ORIGINAL .xlsx ZIP at the end so charts/fonts/colors/macros stay intact.
    const cellPatches: import("@/lib/patchEstWorkbook").EstCellPatch[] = [];

    let writtenRows = 0;
    // Scan columns A–F (indices 0..5) for a Symbol/ISIN match. Some templates put the
    // ticker/ISIN in columns other than A/B (e.g. col C = ticker, col E = ISIN), and the
    // user-visible columns G–J are reserved for Price/52WH/52WL/Spread output, so we
    // never look past column F for matching keys.
    const MATCH_COL_COUNT = 6;
    for (let r = 1; r < aoa.length; r++) {
      const row = aoa[r] || [];
      const candidates: string[] = [];
      for (let c = 0; c < MATCH_COL_COUNT; c++) {
        const raw = String(row[c] ?? "").trim();
        if (!raw) continue;
        // Use the same key normalization as the quote map so symbols, ISINs, and
        // Yahoo-suffixed tickers (e.g. "ABC.PA") all collapse to the same lookup keys.
        currencyLookupKeys(raw).forEach((k) => candidates.push(k));
      }
      if (candidates.length === 0) continue; // skip blank rows

      let q: QuoteResult | undefined;
      let matchedOn = "";
      for (const c of candidates) {
        if (quoteMap.has(c)) {
          q = quoteMap.get(c);
          matchedOn = c;
          break;
        }
      }
      const rowRef = r + 1; // 1-indexed Excel row
      if (!q) {
        // Show the raw cells (A–F) so the user can see what was actually scanned.
        const rawCells = Array.from({ length: MATCH_COL_COUNT }, (_, c) =>
          String(row[c] ?? "").trim()
        ).filter(Boolean);
        sheetRowsNotMatched.push({ row: rowRef, values: rawCells });
        continue;
      }
      if (!q.success) {
        sheetRowsNotMatched.push({ row: rowRef, values: [...candidates, `(quote failed: ${q.error || "unknown"})`] });
        usedQuoteKeys.add(q.symbol.toUpperCase());
        continue;
      }
      // Excel cells: G = col 6 (Price), H = col 7 (52W High), I = col 8 (52W Low),
      // J = col 9 (Spread to 52W High, %). Col D currency stays untouched.
      // We collect patches and apply them in-place to the original .xlsx ZIP at
      // the end — this preserves charts, fonts, colors, conditional formatting,
      // pivots, images, macros, and column number formats from the user's
      // template (which SheetJS would otherwise strip on round-trip).
      const populated: ("G" | "H" | "I" | "J" | "W")[] = [];
      const missing: string[] = [];
      const collectIfFinite = (col: "G" | "H" | "I" | "J" | "W", value: unknown, label: string) => {
        if (typeof value === "number" && Number.isFinite(value)) {
          cellPatches.push({ row: rowRef, col, value });
          populated.push(col);
          columnsWritten[col]++;
        } else {
          missing.push(label);
        }
      };
      collectIfFinite("G", q.price, "Price");
      collectIfFinite("H", q.high52, "52W High");
      collectIfFinite("I", q.low52, "52W Low");
      collectIfFinite("J", q.spread52High, "Spread%");
      // W = daily change % vs previous close. Only write when we have a valid
      // previous close, otherwise skip (don't overwrite the cell with a stale 0).
      if (q.prevClose && q.prevClose > 0) {
        collectIfFinite("W", q.changePercent, "Change%");
      } else {
        missing.push("Change%");
      }
      writtenRows++;
      matched.push({ symbol: q.symbol, isin: q.isin, row: rowRef, matchedOn, populated, missing, price: q.price, prevClose: q.prevClose, changePercent: q.changePercent, priceBasis: q.priceBasis, currency: q.currency });
      usedQuoteKeys.add(q.symbol.toUpperCase());
    }

    const quotesNotInSheet: { symbol: string; isin: string; reason: string }[] = quotes
      .filter((q) => !usedQuoteKeys.has(q.symbol.toUpperCase()))
      .map((q) => ({
        symbol: q.symbol,
        isin: q.isin,
        reason: q.success ? "Symbol/ISIN not found in realtimedata sheet" : `Quote failed: ${q.error || "unknown"}`,
      }));

    // --- PRVFIN tab: write the exchange/index name (Yahoo's fullExchangeName)
    // into column C of rows 336–356, looked up by the symbol/ISIN in column A.
    // This runs against the live workbook the user uploaded; results are passed
    // to patchEstWorkbook as text patches so the original styling is preserved.
    const prvfinTextPatches: import("@/lib/patchEstWorkbook").EstTextPatch[] = [];
    const prvfinSheetName = wb.SheetNames.find(
      (n) => n.replace(/[^a-z0-9]/gi, "").toLowerCase() === "prvfin",
    );
    let prvfinUpdated = 0;
    if (prvfinSheetName) {
      const prvSheet = wb.Sheets[prvfinSheetName];
      const prvAoa = XLSX.utils.sheet_to_json<unknown[]>(prvSheet, { header: 1, defval: "" });
      for (let excelRow = 336; excelRow <= 356; excelRow++) {
        const row = prvAoa[excelRow - 1] || [];
        const keyA = String(row[0] ?? "").trim();
        if (!keyA) continue;
        const lookups = currencyLookupKeys(keyA);
        let q: QuoteResult | undefined;
        for (const k of lookups) {
          const hit = quoteMap.get(k);
          if (hit) { q = hit; break; }
        }
        if (!q || !q.success) continue;
        const exch = String(q.exchange ?? "").trim();
        if (!exch) continue;
        prvfinTextPatches.push({ row: excelRow, col: "C", value: exch });
        prvfinUpdated++;
      }
    }

    // Apply patches IN PLACE to the original .xlsx/.xlsm bytes. This preserves
    // every other tab, every chart, every formula, every font/color/border, and
    // — for .xlsm — the embedded VBA project. SheetJS round-tripping would lose
    // most of that, so we never re-emit the workbook through XLSX.write here.
    const isMacroEnabled = /\.xlsm$/i.test(estFileName);
    let patchedBytes: Uint8Array;
    try {
      const result = await patchEstWorkbook(
        estOriginalBytesRef.current,
        cellPatches,
        prvfinSheetName ? { [prvfinSheetName]: prvfinTextPatches } : undefined,
      );
      patchedBytes = result.bytes;
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : "Could not patch the EST workbook in place.",
        variant: "destructive",
      });
      return;
    }
    const blob = new Blob([patchedBytes.buffer.slice(patchedBytes.byteOffset, patchedBytes.byteOffset + patchedBytes.byteLength) as ArrayBuffer], {
      type: isMacroEnabled
        ? "application/vnd.ms-excel.sheet.macroEnabled.12"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const ext = isMacroEnabled ? "xlsm" : "xlsx";
    const baseName = estFileName.replace(/\.(xlsx|xlsm|xls)$/i, "");
    const downloadName = `${baseName}_updated_${ts}.${ext}`;
    downloadBlob(blob, downloadName);

    // Keep last 5 generated files for re-download.
    setEstExportHistory((prev) =>
      [{ fileName: downloadName, createdAt: new Date(), size: blob.size, blob, rows: writtenRows }, ...prev].slice(0, 5)
    );

    setExportReport({
      exportedAt: new Date(),
      sheetName,
      fileName: downloadName,
      sheetRows: Math.max(0, aoa.length - 1),
      matched,
      quotesNotInSheet,
      sheetRowsNotMatched,
      columnsWritten,
    });

    toast({
      title: "Exported",
      description: `${writtenRows} rows updated · ${quotesNotInSheet.length} quote${quotesNotInSheet.length === 1 ? "" : "s"} unmatched · ${sheetRowsNotMatched.length} sheet row${sheetRowsNotMatched.length === 1 ? "" : "s"} skipped${prvfinSheetName ? ` · PRVFIN col C: ${prvfinUpdated}/21 rows` : ""}.`,
    });
  };
  // Keep ref pointed at the latest closure so the auto-export effect can fire it.
  useEffect(() => {
    exportToEstRef.current = exportToEst;
  });

  const successCount = useMemo(() => quotes.filter((q) => q.success).length, [quotes]);
  const progress = quotes.length > 0 ? (successCount / quotes.length) * 100 : 0;

  const fmt = (n: number, d = 2) =>
    Number.isFinite(n) && n > 0 ? n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }) : "—";

  const mm = String(Math.floor(secondsUntilNext / 60)).padStart(2, "0");
  const ss = String(secondsUntilNext % 60).padStart(2, "0");

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                <FileSpreadsheet className="h-7 w-7 text-primary" />
                Stock Price Gathering Dashboard
              </h1>
              <p className="text-sm text-muted-foreground">
                Live Yahoo Finance quotes (no API key) — refreshes every 10 minutes
              </p>
            </div>
          </div>
        </div>

        {/* File uploads */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="h-4 w-4" /> 1. FileList (Symbol + ISIN)
              </CardTitle>
              <CardDescription>
                Excel file with symbol in column A and ISIN code in column B
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                onChange={(e) => e.target.files?.[0] && handleFileList(e.target.files[0])}
              />
              {fileListName && (
                <div className="text-xs text-muted-foreground">
                  Loaded: <span className="font-medium text-foreground">{fileListName}</span> ·{" "}
                  {stocks.length} stocks
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="h-4 w-4" /> 2. EST Workbook (output)
              </CardTitle>
              <CardDescription>
                Workbook with a <code className="text-xs">realtimedata</code> tab — written to <strong>G</strong>=Price, <strong>H</strong>=52W High, <strong>I</strong>=52W Low, <strong>J</strong>=Spread to 52WH (%). Col D currency stays as your authoritative source.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                onChange={(e) => e.target.files?.[0] && handleEstFile(e.target.files[0])}
              />
              {estFileName && (
                <div className="text-xs text-muted-foreground">
                  Loaded: <span className="font-medium text-foreground">{estFileName}</span>
                </div>
              )}
              {estValidation && (
                <div
                  className={`rounded-md border p-3 text-xs space-y-1 ${
                    estValidation.ok
                      ? "border-emerald-500/30 bg-emerald-500/5"
                      : "border-rose-500/40 bg-rose-500/5"
                  }`}
                >
                  <div className="flex items-center gap-2 font-medium">
                    {estValidation.ok ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        <span className="text-emerald-700 dark:text-emerald-400">Template valid</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-rose-500" />
                        <span className="text-rose-700 dark:text-rose-400">Template invalid — export disabled</span>
                      </>
                    )}
                  </div>
                  <div className="text-muted-foreground">
                    Tab:{" "}
                    {estValidation.sheetFound ? (
                      <code className="text-foreground">{estValidation.sheetName}</code>
                    ) : (
                      <span className="text-rose-500">missing "realtimedata"</span>
                    )}
                    {estValidation.sheetFound && <> · {estValidation.dataRows} data rows</>}
                  </div>
                  <div className="text-muted-foreground">
                    Columns:{" "}
                    {REQUIRED_COLS.map((c) => {
                      const ok = estValidation.columnsPresent.includes(c);
                      return (
                        <Badge
                          key={c}
                          variant="outline"
                          className={`mr-1 ${
                            ok
                              ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
                              : "border-rose-500/40 text-rose-700 dark:text-rose-400"
                          }`}
                        >
                          {c} {REQUIRED_COL_LABELS[c]} {ok ? "✓" : "✗"}
                        </Badge>
                      );
                    })}
                  </div>
                  {!estValidation.ok && estValidation.warnings.length > 0 && (
                    <ul className="list-disc list-inside text-rose-700 dark:text-rose-400">
                      {estValidation.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  )}
                  {!estValidation.sheetFound && estValidation.availableSheets.length > 0 && (
                    <div className="text-muted-foreground">
                      Available tabs: {estValidation.availableSheets.map((s) => `"${s}"`).join(", ")}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="pt-6 flex flex-wrap items-center gap-3">
            <Button onClick={fetchQuotes} disabled={loading || stocks.length === 0}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Fetching…" : "Refresh now"}
            </Button>
            {(() => {
              const failedCount = quotes.filter((q) => !q.success).length;
              return (
                <Button
                  onClick={retryFailedQuotes}
                  disabled={loading || failedCount === 0}
                  variant="outline"
                  title={failedCount === 0 ? "No failed quotes to retry" : `Retry ${failedCount} failed quote(s)`}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Retry failed{failedCount > 0 ? ` (${failedCount})` : ""}
                </Button>
              );
            })()}
            <Button
              variant="ghost"
              onClick={() => setRetryReportOpen(true)}
              disabled={!retryReport}
              title={retryReport ? "View per-symbol retry report" : "Run a retry to see a report"}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Retry report
              {retryReport && (retryReport.stillFailing.length + retryReport.missingFromResponse.length) > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {retryReport.stillFailing.length + retryReport.missingFromResponse.length}
                </Badge>
              )}
            </Button>
            <Button
              variant={autoRefresh ? "secondary" : "outline"}
              onClick={() => setAutoRefresh((v) => !v)}
              disabled={stocks.length === 0}
            >
              <Clock className="h-4 w-4 mr-2" />
              Auto-refresh every {refreshIntervalMinutes} min: {autoRefresh ? "ON" : "OFF"}
            </Button>
            <div className="flex items-center gap-2">
              <Label htmlFor="refresh-interval" className="text-xs text-muted-foreground">
                Interval
              </Label>
              <Select
                value={String(refreshIntervalMinutes)}
                onValueChange={(v) => {
                  const mins = Number(v);
                  setRefreshIntervalMinutes(mins);
                  setSecondsUntilNext(mins * 60);
                  try {
                    window.sessionStorage.setItem(REFRESH_INTERVAL_STORAGE_KEY, String(mins));
                  } catch {
                    // sessionStorage unavailable — non-fatal
                  }
                }}
              >
                <SelectTrigger id="refresh-interval" className="h-9 w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REFRESH_INTERVAL_OPTIONS.map((m) => (
                    <SelectItem key={m} value={String(m)}>
                      {m} minutes
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => { void exportToEst(); }}
              disabled={!estWorkbook || quotes.length === 0 || (estValidation !== null && !estValidation.ok)}
              variant="default"
              title={estValidation && !estValidation.ok ? "Fix template validation errors before exporting" : undefined}
            >
              <Download className="h-4 w-4 mr-2" /> Export to EST workbook
            </Button>
            <Button
              variant={autoExportEst ? "secondary" : "outline"}
              onClick={() => setAutoExportEst((v) => !v)}
              disabled={!estWorkbook}
              title={
                !estWorkbook
                  ? "Load an EST workbook first (use the '2. EST Workbook (output)' uploader above)"
                  : "After every auto-refresh, write a fresh EST_updated_<time>.xlsx with G=Price · H=52WH · I=52WL · J=Spread%. Col D currency stays untouched."
              }
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Auto-export EST: {autoExportEst ? "ON" : "OFF"}
            </Button>
            {!estWorkbook && (
              <Badge
                variant="outline"
                className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 gap-1"
                title="Upload an EST workbook (.xlsx with a 'realtimedata' tab) in the '2. EST Workbook (output)' card above to enable auto-export."
              >
                <AlertCircle className="h-3.5 w-3.5" />
                Load an EST workbook to enable
              </Badge>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={estExportHistory.length === 0} title="Re-download a recently generated EST_updated file">
                  <History className="h-4 w-4 mr-2" />
                  Recent EST files
                  {estExportHistory.length > 0 && (
                    <Badge variant="secondary" className="ml-2">{estExportHistory.length}</Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[420px]">
                <DropdownMenuLabel>Last {estExportHistory.length} generated file{estExportHistory.length === 1 ? "" : "s"}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      autoFocus
                      value={estHistoryFilter}
                      onChange={(e) => setEstHistoryFilter(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="Filter by filename…"
                      className="h-8 pl-7 text-xs"
                    />
                  </div>
                </div>
                <DropdownMenuSeparator />
                {(() => {
                  const q = estHistoryFilter.trim().toLowerCase();
                  const filtered = q
                    ? estExportHistory.filter((f) => f.fileName.toLowerCase().includes(q))
                    : estExportHistory;
                  if (estExportHistory.length === 0) {
                    return <DropdownMenuItem disabled>No files generated yet</DropdownMenuItem>;
                  }
                  if (filtered.length === 0) {
                    return <DropdownMenuItem disabled>No files match “{estHistoryFilter}”</DropdownMenuItem>;
                  }
                  return filtered.map((f) => {
                    const hasBlob = !!f.blob;
                    return (
                      <DropdownMenuItem
                        key={f.fileName}
                        disabled={!hasBlob}
                        onClick={() => hasBlob && downloadBlob(f.blob!, f.fileName)}
                        className="flex flex-col items-start gap-0.5 cursor-pointer"
                        title={hasBlob ? "Re-download" : "Restored from previous session — file no longer in memory"}
                      >
                        <span className="font-mono text-xs truncate w-full">{f.fileName}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {f.createdAt.toLocaleTimeString()} · {f.rows} rows · {(f.size / 1024).toFixed(1)} KB
                          {!hasBlob && " · metadata only"}
                        </span>
                      </DropdownMenuItem>
                    );
                  });
                })()}
              </DropdownMenuContent>
            </DropdownMenu>

            {autoExportStatus && (() => {
              const { columnsWritten: cw, fullyPopulatedRows, partialRows, writtenRows, ranAt, fileName } = autoExportStatus;
              const hasPartial = partialRows.length > 0;
              const allFour = cw.G === writtenRows && cw.H === writtenRows && cw.I === writtenRows && cw.J === writtenRows;
              return (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className={
                          hasPartial
                            ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400 gap-1 cursor-help"
                            : "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 gap-1 cursor-help"
                        }
                      >
                        {hasPartial ? <AlertCircle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                        Auto-redistribution {hasPartial ? `OK · ${partialRows.length} partial` : "OK"} · {writtenRows} rows · {ranAt.toLocaleTimeString()}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-md text-xs space-y-1.5">
                      <div className="font-semibold">EST RealTime columns G–J + W populated</div>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 font-mono">
                        <div>G (Price): {cw.G}/{writtenRows}</div>
                        <div>H (52W High): {cw.H}/{writtenRows}</div>
                        <div>I (52W Low): {cw.I}/{writtenRows}</div>
                        <div>J (Spread%): {cw.J}/{writtenRows}</div>
                        <div>W (Δ % vs prev close): {cw.W}/{writtenRows}</div>
                      </div>
                      <div className={allFour ? "text-emerald-500" : "text-amber-500"}>
                        {allFour
                          ? `✓ All 4 expected columns filled for every one of ${writtenRows} matched rows.`
                          : `${fullyPopulatedRows}/${writtenRows} rows fully populated · ${partialRows.length} with missing fields.`}
                      </div>
                      {hasPartial && (
                        <div className="border-t border-border/50 pt-1.5">
                          <div className="font-semibold mb-0.5">Rows with missing quote fields:</div>
                          <ul className="space-y-0.5 max-h-40 overflow-auto">
                            {partialRows.slice(0, 12).map((p) => (
                              <li key={p.row} className="font-mono">
                                Row {p.row} · {p.symbol} — missing: {p.missing.join(", ")}
                              </li>
                            ))}
                            {partialRows.length > 12 && (
                              <li className="italic opacity-70">…and {partialRows.length - 12} more</li>
                            )}
                          </ul>
                        </div>
                      )}
                      <div className="border-t border-border/50 pt-1 opacity-70 truncate">File: {fileName}</div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })()}

            <div className="ml-auto flex items-center gap-3 text-sm text-muted-foreground">
              {lastRefresh && <span>Last refresh: {lastRefresh.toLocaleTimeString()}</span>}
              {autoRefresh && (
                <Badge variant="outline">
                  Next in {mm}:{ss}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Manual ticker mappings — overrides Yahoo resolution per user */}
        <TickerMappingsManager
          prefill={mappingPrefill}
          onPrefillConsumed={() => setMappingPrefill(null)}
          onChange={() => { /* mappings refresh on next fetch */ }}
        />

        {/* Results */}
        {quotes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between gap-3 flex-wrap">
                <span>Live Quotes</span>
                <div className="flex items-center gap-3">
                  {estOverrideStatus && (
                    <Badge
                      variant={estOverrideStatus.overridden > 0 ? "default" : "outline"}
                      className="text-[10px] font-normal gap-1"
                      title={`EST currency override ran on ${estOverrideStatus.scope} fetch at ${estOverrideStatus.at.toLocaleTimeString()} — ${estOverrideStatus.overridden}/${estOverrideStatus.total} row(s) overridden from realtimedata col D`}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      EST override · {estOverrideStatus.overridden}/{estOverrideStatus.total}
                      <span className="text-muted-foreground/70 ml-1">({estOverrideStatus.scope})</span>
                    </Badge>
                  )}
                  <div
                    className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground"
                    title={
                      "Price basis for Δ %.\n" +
                      "• Raw: change vs Yahoo's reported previous close (matches Yahoo headline; can spike on splits/dividends).\n" +
                      "• Adjusted: change vs split- and dividend-adjusted prior close (total-return basis; eliminates ex-day artifacts)."
                    }
                  >
                    <span>Basis:</span>
                    <Select value={priceBasis} onValueChange={(v) => setPriceBasis(v as "raw" | "adjusted")}>
                      <SelectTrigger className="h-7 w-[140px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="raw">Raw close</SelectItem>
                        <SelectItem value="adjusted">Adjusted (split/div)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-normal text-muted-foreground cursor-pointer">
                    <Switch
                      checked={onlyEstCurrency}
                      onCheckedChange={setOnlyEstCurrency}
                      aria-label="Show only rows where currency came from EST realtimedata"
                    />
                    <span>EST currency only</span>
                  </label>
                  <div
                    className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground"
                    title="Δ % color thresholds. Green when change ≥ positive threshold; red when change ≤ negative threshold; neutral otherwise."
                  >
                    <span>Δ % thresh:</span>
                    <Input
                      type="number"
                      step="0.1"
                      value={deltaPosThreshold}
                      onChange={(e) => setDeltaPosThreshold(parseFloat(e.target.value) || 0)}
                      className="h-7 w-16 text-xs text-emerald-600 dark:text-emerald-400 font-mono"
                      aria-label="Positive Δ % threshold (green)"
                    />
                    <span>/</span>
                    <Input
                      type="number"
                      step="0.1"
                      value={deltaNegThreshold}
                      onChange={(e) => setDeltaNegThreshold(parseFloat(e.target.value) || 0)}
                      className="h-7 w-16 text-xs text-rose-600 dark:text-rose-400 font-mono"
                      aria-label="Negative Δ % threshold (red)"
                    />
                    <span>%</span>
                  </div>
                  <span className="text-sm font-normal text-muted-foreground">
                    {successCount}/{quotes.length} successful
                  </span>
                </div>
              </CardTitle>
              <Progress value={progress} className="h-1.5 mt-2" />
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Symbol</TableHead>
                      <TableHead>ISIN</TableHead>
                      <TableHead>Resolved</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead className="text-right" title={`Daily change vs previous close (basis: ${priceBasis === "adjusted" ? "split & dividend adjusted" : "raw close"})`}>
                        Δ %{priceBasis === "adjusted" ? <span className="text-[9px] text-muted-foreground ml-1">adj</span> : null}
                      </TableHead>
                      <TableHead>Curr.</TableHead>
                      <TableHead className="text-right">52W High</TableHead>
                      <TableHead className="text-right">52W Low</TableHead>
                      <TableHead className="text-right">Spread 52WH</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes
                      .filter((q) => !onlyEstCurrency || !!lookupEstCurrency(expectedCurrencyMap, q))
                      .map((q, i) => (
                      <TableRow key={`${q.symbol}-${i}`}>
                        <TableCell className="font-medium">{q.symbol}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{q.isin || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {q.resolvedTicker ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="inline-flex items-center gap-1.5">
                                <span className="font-mono">{q.resolvedTicker}</span>
                                {(() => {
                                  const url = tradingViewUrl(q.resolvedTicker);
                                  return url ? (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors"
                                      title={`Preview ${q.resolvedTicker} on TradingView`}
                                      aria-label={`Preview ${q.resolvedTicker} on TradingView`}
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                    </a>
                                  ) : null;
                                })()}
                                {q.resolvedVia === "isin_search" && (
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">ISIN</Badge>
                                )}
                                {q.isinMismatch && q.isinExpectedTicker && (
                                  <Badge
                                    variant="destructive"
                                    className="text-[10px] px-1 py-0 h-4 gap-0.5"
                                    title={`ISIN mismatch — Yahoo's search for ISIN ${q.isin} returns "${q.isinExpectedTicker}", but data was fetched from "${q.resolvedTicker}". You may be looking at a different security. Add a manual mapping to override.`}
                                  >
                                    <AlertCircle className="h-2.5 w-2.5" />
                                    ISIN ≠ {q.isinExpectedTicker}
                                  </Badge>
                                )}
                              </span>
                              {q.name && (
                                <span className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={q.name}>
                                  {q.name}
                                </span>
                              )}
                              {q.isinMismatch && q.isinExpectedTicker && (
                                <span className="text-[10px] text-destructive">
                                  ⚠ ISIN points to <span className="font-mono">{q.isinExpectedTicker}</span>
                                </span>
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono">{fmt(q.price)}</TableCell>
                        <TableCell
                          className={`text-right font-mono ${
                            q.success && typeof q.changePercent === "number"
                              ? q.changePercent >= deltaPosThreshold
                                ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                                : q.changePercent <= deltaNegThreshold
                                  ? "text-rose-600 dark:text-rose-400 font-semibold"
                                  : "text-muted-foreground"
                              : "text-muted-foreground"
                          }`}
                          title={
                            q.success && q.prevClose && q.prevClose > 0 && typeof q.price === "number"
                              ? [
                                  `Basis: ${(q.priceBasis || priceBasis) === "adjusted" ? "Adjusted close (split & dividend folded into prevClose)" : "Raw close (Yahoo headline)"}`,
                                  `Previous close: ${fmt(q.prevClose)} ${q.currency || ""}`,
                                  `Current price:  ${fmt(q.price)} ${q.currency || ""}`,
                                  `Formula: ((current − prevClose) / prevClose) × 100`,
                                  `       = ((${fmt(q.price)} − ${fmt(q.prevClose)}) / ${fmt(q.prevClose)}) × 100`,
                                  `       = ${typeof q.changePercent === "number" ? q.changePercent.toFixed(4) : "—"}%`,
                                  ``,
                                  `Thresholds: green ≥ ${deltaPosThreshold}% · red ≤ ${deltaNegThreshold}%`,
                                  q.splitOnPrevDay
                                    ? `\n⚠ Split on ex-day${q.splitRatio ? ` (ratio ${q.splitRatio})` : ""} — switch to Adjusted basis to neutralize.`
                                    : "",
                                  q.dividendOnPrevDay
                                    ? `\n⚠ Dividend on ex-day${q.dividendAmount ? ` (${q.dividendAmount} ${q.currency || ""})` : ""} — switch to Adjusted basis to neutralize.`
                                    : "",
                                ]
                                  .filter(Boolean)
                                  .join("\n")
                              : "No previous close available"
                          }
                        >
                          {q.success && typeof q.changePercent === "number" && q.prevClose && q.prevClose > 0 ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              <span>{`${q.changePercent > 0 ? "+" : ""}${q.changePercent.toFixed(2)}%`}</span>
                              {(q.splitOnPrevDay || q.dividendOnPrevDay) && (
                                <span
                                  className="text-[9px] px-1 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400 font-semibold"
                                  title={`Corporate event on ex-day: ${q.splitOnPrevDay ? "split" : ""}${q.splitOnPrevDay && q.dividendOnPrevDay ? "+" : ""}${q.dividendOnPrevDay ? "div" : ""}`}
                                >
                                  {q.splitOnPrevDay ? "S" : ""}{q.dividendOnPrevDay ? "D" : ""}
                                </span>
                              )}
                            </span>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const est = lookupEstCurrency(expectedCurrencyMap, q);
                            const src = est ? lookupEstCurrencySource(expectedCurrencySourceMap, q) : null;
                            const display = est || (q.currency || "").toUpperCase();
                            const fromEst = !!est;
                            const matchedFieldLabel: Record<string, string> = {
                              symbol: "Symbol",
                              isin: "ISIN",
                              resolvedTicker: "Resolved ticker",
                            };
                            const tooltip = !fromEst
                              ? "No EST currency loaded — showing Yahoo currency"
                              : src
                                ? `Currency ${est} from EST realtimedata col D\n` +
                                  `Matched on quote ${matchedFieldLabel[src.matchedOn]} ("${src.matchedKey}")\n` +
                                  `EST source: column ${src.col} (${src.colName}) = "${src.value}"`
                              : `Currency from EST realtimedata col D (${est})`;
                            const badgeLabel = fromEst && src
                              ? src.colName === "ISIN" ? "ISIN" : src.colName === "Symbol" ? "SYM" : "EST"
                              : "EST";
                            return (
                              <Badge
                                variant="outline"
                                className="text-xs w-fit whitespace-pre-line"
                                title={tooltip}
                              >
                                {display || "—"}
                                {fromEst && (
                                  <span className="ml-1 text-[9px] text-muted-foreground">{badgeLabel}</span>
                                )}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right font-mono text-emerald-600 dark:text-emerald-400">
                          <span className="inline-flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" /> {fmt(q.high52)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono text-rose-600 dark:text-rose-400">
                          <span className="inline-flex items-center gap-1">
                            <TrendingDown className="h-3 w-3" /> {fmt(q.low52)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {q.success && q.high52 > 0 ? `${q.spread52High.toFixed(2)}%` : "—"}
                        </TableCell>
                        <TableCell>
                          {q.success ? (
                            <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10">
                              OK
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="text-xs">
                              {q.error || "Failed"}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {snapshots.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <History className="h-4 w-4" /> Snapshot History
                  <Badge variant="outline" className="ml-2">{snapshots.length}</Badge>
                </span>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={downloadAllSnapshotsZip}>
                    <Archive className="h-4 w-4 mr-1" /> Download all (ZIP)
                  </Button>
                  <Button size="sm" variant="ghost" onClick={clearSnapshots}>
                    <Trash2 className="h-4 w-4 mr-1" /> Clear
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                Each refresh captures a CSV snapshot (price, 52W high/low, spread). Stored in-memory for this session — download to keep history.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Captured at</TableHead>
                      <TableHead className="text-right">Stocks</TableHead>
                      <TableHead className="text-right">OK</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {snapshots.map((s, i) => (
                      <TableRow key={s.takenAt.toISOString()}>
                        <TableCell className="font-mono text-xs">{s.takenAt.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{s.rows}</TableCell>
                        <TableCell className="text-right text-emerald-600 dark:text-emerald-400">{s.ok}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => downloadSnapshot(i)}>
                            <Download className="h-4 w-4 mr-1" /> CSV
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {stocks.length === 0 && (
          <Card>
            <CardContent className="pt-6 text-center text-sm text-muted-foreground">
              <Label className="text-base font-medium block mb-2">Get started</Label>
              Upload your <strong>FileList</strong> Excel (symbol in column A, ISIN in column B) and your{" "}
              <strong>EST workbook</strong> (must contain a <code>realtimedata</code> tab). Then click{" "}
              <em>Refresh now</em> and <em>Export to EST workbook</em> to download the updated file with prices in
              columns G/H/I.
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={!!exportReport} onOpenChange={(o) => !o && setExportReport(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
              Export Summary Report
            </DialogTitle>
            <DialogDescription>
              {exportReport && (
                <>
                  Exported <span className="font-mono">{exportReport.fileName}</span> at{" "}
                  {exportReport.exportedAt.toLocaleTimeString()} · sheet{" "}
                  <code className="text-xs">{exportReport.sheetName}</code> ({exportReport.sheetRows} data rows)
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {exportReport && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card className="border-emerald-500/30">
                  <CardContent className="pt-4 text-center">
                    <CheckCircle2 className="h-5 w-5 mx-auto text-emerald-500 mb-1" />
                    <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{exportReport.matched.length}</div>
                    <div className="text-xs text-muted-foreground">Rows updated</div>
                  </CardContent>
                </Card>
                <Card className="border-amber-500/30">
                  <CardContent className="pt-4 text-center">
                    <AlertCircle className="h-5 w-5 mx-auto text-amber-500 mb-1" />
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{exportReport.quotesNotInSheet.length}</div>
                    <div className="text-xs text-muted-foreground">Quotes unmatched</div>
                  </CardContent>
                </Card>
                <Card className="border-rose-500/30">
                  <CardContent className="pt-4 text-center">
                    <XCircle className="h-5 w-5 mx-auto text-rose-500 mb-1" />
                    <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">{exportReport.sheetRowsNotMatched.length}</div>
                    <div className="text-xs text-muted-foreground">Sheet rows skipped</div>
                  </CardContent>
                </Card>
              </div>

              {exportReport.matched.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Matched & written ({exportReport.matched.length})
                  </div>
                  <div className="rounded-md border max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Symbol</TableHead>
                          <TableHead>ISIN</TableHead>
                          <TableHead>Matched on</TableHead>
                          <TableHead className="text-right">Sheet row</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                          <TableHead className="text-right">Prev close</TableHead>
                          <TableHead
                            className="text-right cursor-pointer select-none hover:text-foreground"
                            title="Click to sort by Change %. Cycles: none → desc → asc → none."
                            onClick={() =>
                              setMatchedSortDir((d) => (d === "none" ? "desc" : d === "desc" ? "asc" : "none"))
                            }
                          >
                            <span className="inline-flex items-center justify-end gap-1">
                              Change % → W
                              {matchedSortDir === "none" && <ArrowUpDown className="h-3 w-3 opacity-50" />}
                              {matchedSortDir === "desc" && <ArrowDown className="h-3 w-3" />}
                              {matchedSortDir === "asc" && <ArrowUp className="h-3 w-3" />}
                            </span>
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(matchedSortDir === "none"
                          ? exportReport.matched
                          : [...exportReport.matched].sort((a, b) => {
                              const av = typeof a.changePercent === "number" ? a.changePercent : null;
                              const bv = typeof b.changePercent === "number" ? b.changePercent : null;
                              if (av === null && bv === null) return 0;
                              if (av === null) return 1; // nulls last
                              if (bv === null) return -1;
                              return matchedSortDir === "asc" ? av - bv : bv - av;
                            })
                        ).map((m, i) => {
                          const wWritten = m.populated.includes("W");
                          const cp = m.changePercent;
                          const cpClass =
                            typeof cp === "number"
                              ? cp >= deltaPosThreshold
                                ? "text-emerald-600 dark:text-emerald-400 font-semibold"
                                : cp <= deltaNegThreshold
                                  ? "text-rose-600 dark:text-rose-400 font-semibold"
                                  : "text-muted-foreground"
                              : "text-muted-foreground";
                          return (
                            <TableRow key={`m-${i}`}>
                              <TableCell className="font-medium">{m.symbol}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{m.isin || "—"}</TableCell>
                              <TableCell className="text-xs font-mono">{m.matchedOn}</TableCell>
                              <TableCell className="text-right font-mono">{m.row}</TableCell>
                              <TableCell className="text-right font-mono text-xs">
                                {typeof m.price === "number" ? m.price.toFixed(2) : "—"}
                              </TableCell>
                              <TableCell className="text-right font-mono text-xs text-muted-foreground">
                                {typeof m.prevClose === "number" && m.prevClose > 0 ? m.prevClose.toFixed(2) : "—"}
                              </TableCell>
                              <TableCell
                                className={`text-right font-mono text-xs ${cpClass}`}
                                title={
                                  typeof cp === "number" && m.prevClose && m.prevClose > 0
                                    ? `Basis: ${m.priceBasis === "adjusted" ? "Adjusted (split/div)" : "Raw"}\n` +
                                      `((${m.price} − ${m.prevClose}) / ${m.prevClose}) × 100 = ${cp.toFixed(4)}%\n` +
                                      `Excel cell: W${m.row} ${wWritten ? "✓ written" : "✗ skipped"}`
                                    : "No previous close — column W skipped"
                                }
                              >
                                {typeof cp === "number" && m.prevClose && m.prevClose > 0
                                  ? `${cp > 0 ? "+" : ""}${cp.toFixed(2)}%`
                                  : "—"}
                                {!wWritten && (
                                  <span className="ml-1 text-[9px] px-1 rounded bg-muted text-muted-foreground">skip</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {exportReport.quotesNotInSheet.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" /> Quotes with no matching sheet row ({exportReport.quotesNotInSheet.length})
                  </div>
                  <div className="rounded-md border max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Symbol</TableHead>
                          <TableHead>ISIN</TableHead>
                          <TableHead>Reason</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {exportReport.quotesNotInSheet.map((u, i) => (
                          <TableRow key={`u-${i}`}>
                            <TableCell className="font-medium">{u.symbol}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{u.isin || "—"}</TableCell>
                            <TableCell className="text-xs">{u.reason}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {exportReport.sheetRowsNotMatched.length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-2 flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-rose-500" /> Sheet rows with no matching quote ({exportReport.sheetRowsNotMatched.length})
                  </div>
                  <div className="rounded-md border max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">Sheet row</TableHead>
                          <TableHead>Values (col A / B)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {exportReport.sheetRowsNotMatched.map((s, i) => (
                          <TableRow key={`s-${i}`}>
                            <TableCell className="text-right font-mono">{s.row}</TableCell>
                            <TableCell className="text-xs font-mono">{s.values.join(" · ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Retry per-symbol report */}
      <Dialog open={retryReportOpen} onOpenChange={setRetryReportOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Last retry — per-symbol report
            </DialogTitle>
            <DialogDescription>
              {retryReport && (
                <>
                  Retried <span className="font-mono">{retryReport.attempted}</span> failed quote(s) at{" "}
                  {retryReport.retriedAt.toLocaleTimeString()}.
                </>
              )}
            </DialogDescription>
            {retryReport && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const esc = (v: string | number) => {
                      const s = String(v ?? "");
                      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    };
                    const header = ["symbol", "isin", "previous_status", "retry_status", "error_message", "price"];
                    const rows: (string | number)[][] = [];
                    for (const r of retryReport.recovered) {
                      rows.push([r.symbol, r.isin, "failed", "success", "", r.price]);
                    }
                    for (const s of retryReport.stillFailing) {
                      rows.push([s.symbol, s.isin, `failed: ${s.previousError}`, "failed", s.currentError, ""]);
                    }
                    for (const m of retryReport.missingFromResponse) {
                      rows.push([m.symbol, m.isin, `failed: ${m.previousError}`, "missing", "no response from server", ""]);
                    }
                    const csv = [header, ...rows].map((r) => r.map(esc).join(",")).join("\n");
                    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    const ts = retryReport.retriedAt
                      .toISOString()
                      .replace(/[:.]/g, "-")
                      .replace("T", "_")
                      .slice(0, 19);
                    a.href = url;
                    a.download = `retry-report_${ts}.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download CSV
                </Button>
              </div>
            )}
          </DialogHeader>

          {retryReport && (
            <div className="space-y-6">
              {(() => {
                const counts = { notFound: 0, rateLimit: 0, network: 0, noPrice: 0, server: 0, unknown: 0 };
                for (const s of retryReport.stillFailing) {
                  const t = classifyQuoteError(s.currentError).tone;
                  counts[t] = (counts[t] || 0) + 1;
                }
                const chip = (
                  key: typeof diagnosisFilter,
                  label: string,
                  count: number,
                  classes: string,
                ) => {
                  const active = diagnosisFilter === key;
                  return (
                    <button
                      type="button"
                      onClick={() => setDiagnosisFilter(active ? "all" : key)}
                      disabled={count === 0 && key !== "all"}
                      className={`px-2.5 py-1 rounded-md border text-[11px] font-medium transition-all ${classes} ${
                        active ? "ring-2 ring-offset-1 ring-offset-background ring-foreground/40 shadow-sm" : ""
                      } ${count === 0 && key !== "all" ? "opacity-40 cursor-not-allowed" : "hover:scale-[1.03]"}`}
                    >
                      {label} <span className="font-mono ml-1">{count}</span>
                    </button>
                  );
                };
                return (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-xs text-muted-foreground">Recovered</div>
                          <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                            {retryReport.recovered.length}
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-xs text-muted-foreground">Still failing</div>
                          <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">
                            {retryReport.stillFailing.length}
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-4">
                          <div className="text-xs text-muted-foreground">Missing from response</div>
                          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                            {retryReport.missingFromResponse.length}
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {retryReport.stillFailing.length > 0 && (
                      <Card>
                        <CardContent className="pt-4 pb-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              Failure breakdown — click to filter
                            </div>
                            {diagnosisFilter !== "all" && (
                              <button
                                type="button"
                                onClick={() => setDiagnosisFilter("all")}
                                className="text-[10px] text-muted-foreground underline hover:text-foreground"
                              >
                                Clear filter
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {chip("all", "All", retryReport.stillFailing.length, "bg-muted text-foreground border-border")}
                            {chip("notFound", "404 Not found", counts.notFound, "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30")}
                            {chip("rateLimit", "429 Rate-limited", counts.rateLimit, "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30")}
                            {chip("network", "Network / timeout", counts.network, "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30")}
                            {chip("noPrice", "No price", counts.noPrice, "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30")}
                            {chip("server", "Server (5xx)", counts.server, "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30")}
                            {chip("unknown", "Unknown", counts.unknown, "bg-muted text-muted-foreground border-border")}
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                );
              })()}

              {retryReport.stillFailing.length > 0 && (() => {
                const filtered = diagnosisFilter === "all"
                  ? retryReport.stillFailing
                  : retryReport.stillFailing.filter((s) => classifyQuoteError(s.currentError).tone === diagnosisFilter);
                return (
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-rose-500" /> Still failing (
                    {diagnosisFilter === "all"
                      ? retryReport.stillFailing.length
                      : `${filtered.length} of ${retryReport.stillFailing.length}`}
                    )
                  </h4>
                  <div className="border rounded-md max-h-72 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Symbol</TableHead>
                          <TableHead>ISIN</TableHead>
                          <TableHead>Previous error</TableHead>
                          <TableHead>Diagnosis &amp; next action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-xs text-muted-foreground text-center py-4">
                              No rows match the active filter.
                            </TableCell>
                          </TableRow>
                        ) : filtered.map((s, i) => {
                          const rowKey = `sf-${s.symbol}|${s.isin}-${i}`;
                          const isOpen = !!expandedRows[rowKey];
                          return (
                            <Fragment key={rowKey}>
                              <TableRow
                                className="cursor-pointer hover:bg-muted/40"
                                onClick={() =>
                                  setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
                                }
                              >
                                <TableCell className="w-8 p-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    aria-label={isOpen ? "Hide quote response" : "View quote response"}
                                    aria-expanded={isOpen}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
                                    }}
                                  >
                                    {isOpen ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TableCell>
                                <TableCell className="font-mono">{s.symbol}</TableCell>
                                <TableCell className="font-mono text-xs">{s.isin || "—"}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{s.previousError}</TableCell>
                                <TableCell className="text-xs">
                                  {(() => {
                                    const cls = classifyQuoteError(s.currentError);
                                    const toneStyles: Record<string, string> = {
                                      notFound: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
                                      rateLimit: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
                                      server: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
                                      network: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
                                      noPrice: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
                                      unknown: "bg-muted text-muted-foreground border-border",
                                    };
                                    const tried: string[] = s.rawResponse?.triedTickers || [];
                                    const attempts: AttemptRecord[] = s.rawResponse?.attempts || [];
                                    // Smart picker: rank tried tickers by specificity + observed behaviour.
                                    //   +50  has a recognized exchange suffix (e.g. .SW, .DE, .L, .T)
                                    //   +20  has any dot suffix (less common exchange but still specific)
                                    //   +10  per non-404 attempt observed for this ticker (5xx/network = exists, just flaky)
                                    //   -30  observed a hard 404 on this ticker (definitely wrong)
                                    //   +5   later position in the tried list (refined fallback last)
                                    const scoreTicker = (t: string, idx: number): number => {
                                      let score = idx * 5;
                                      const dot = t.lastIndexOf(".");
                                      if (dot !== -1) {
                                        const sfx = t.slice(dot + 1).toUpperCase();
                                        score += YAHOO_SUFFIX_TO_TV_EXCHANGE[sfx] ? 50 : 20;
                                      }
                                      const tickerAttempts = attempts.filter((a) => a.ticker === t);
                                      const had404 = tickerAttempts.some((a) => a.status === 404);
                                      const nonFatal = tickerAttempts.filter((a) => !a.ok && a.status !== 404).length;
                                      if (had404) score -= 30;
                                      score += nonFatal * 10;
                                      return score;
                                    };
                                    const bestTried =
                                      cls.tone === "notFound" && tried.length > 0
                                        ? [...tried]
                                            .map((t, i) => ({ t, s: scoreTicker(t, i) }))
                                            .sort((a, b) => b.s - a.s)[0].t
                                        : undefined;
                                    const lookupQuery = s.symbol || s.isin;
                                    const lookupSource = s.symbol ? "symbol" : s.isin ? "ISIN" : "—";
                                    const yahooSearchUrl = `https://finance.yahoo.com/lookup?s=${encodeURIComponent(lookupQuery)}`;
                                    return (
                                      <div className="space-y-1.5">
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                          <Badge variant="outline" className={`text-[10px] ${toneStyles[cls.tone]}`}>
                                            {cls.label}
                                          </Badge>
                                          <div className="flex gap-1 shrink-0">
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-6 px-2 text-[10px]"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setMappingPrefill({ symbol: s.symbol, isin: s.isin });
                                                setRetryReportOpen(false);
                                              }}
                                            >
                                              <Link2 className="h-3 w-3 mr-1" /> Map
                                            </Button>
                                            {bestTried && (
                                              <>
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-6 px-2 text-[10px]"
                                                  title={`Auto-fill mapping editor with ${bestTried}`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setMappingPrefill({
                                                      symbol: s.symbol,
                                                      isin: s.isin,
                                                      resolved: bestTried,
                                                      notes: `Auto-suggested from 404 retry (tried: ${tried.join(", ")})`,
                                                    });
                                                    setRetryReportOpen(false);
                                                  }}
                                                >
                                                  <Link2 className="h-3 w-3 mr-1" /> Map → {bestTried}
                                                </Button>
                                                <Button
                                                  variant="default"
                                                  size="sm"
                                                  className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                                                  disabled={applyingRow === `${s.symbol}|${s.isin}`}
                                                  title={`Save mapping → ${bestTried} and immediately re-run quote`}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!window.confirm(
                                                      `Apply mapping ${s.symbol || s.isin} → ${bestTried} and re-fetch this quote now?`
                                                    )) return;
                                                    applyMappingAndRefetch(
                                                      { symbol: s.symbol, isin: s.isin },
                                                      bestTried,
                                                      `Auto-applied from 404 retry (tried: ${tried.join(", ")})`,
                                                    );
                                                  }}
                                                >
                                                  {applyingRow === `${s.symbol}|${s.isin}` ? (
                                                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                                                  ) : (
                                                    <Zap className="h-3 w-3 mr-1" />
                                                  )}
                                                  Apply & refetch
                                                </Button>
                                              </>
                                            )}
                                            <TooltipProvider delayDuration={150}>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <a
                                                    href={yahooSearchUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    <Button variant="outline" size="sm" className="h-6 px-2 text-[10px]">
                                                      <ExternalLink className="h-3 w-3 mr-1" /> Search
                                                    </Button>
                                                  </a>
                                                </TooltipTrigger>
                                                <TooltipContent side="top" align="end" className="max-w-md space-y-1.5 p-2.5">
                                                  <div className="text-[11px] font-semibold">Open Yahoo Finance lookup</div>
                                                  <div className="text-[10px] flex items-center gap-1">
                                                    <span className="text-muted-foreground uppercase tracking-wider">Input ({lookupSource}):</span>
                                                    <code className="font-mono px-1.5 py-0.5 rounded bg-muted border border-border">
                                                      {lookupQuery || "—"}
                                                    </code>
                                                  </div>
                                                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">URL</div>
                                                  <code className="block text-[10px] font-mono break-all bg-muted border border-border rounded px-1.5 py-1">
                                                    {yahooSearchUrl}
                                                  </code>
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                          </div>
                                        </div>
                                        <p className="text-[11px] text-muted-foreground leading-snug">{cls.suggestion}</p>
                                        {tried.length > 0 && (
                                           <div className="flex flex-wrap items-center gap-1 pt-0.5">
                                             <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                               Tried ({tried.length}) — click to map:
                                             </span>
                                             {tried.map((t) => {
                                               const tAttempts = attempts.filter((a) => a.ticker === t);
                                               const had404 = tAttempts.some((a) => a.status === 404);
                                               const isBest = t === bestTried;
                                               const score = scoreTicker(t, tried.indexOf(t));
                                               return (
                                                 <TooltipProvider key={t} delayDuration={200}>
                                                   <Tooltip>
                                                     <TooltipTrigger asChild>
                                                       <button
                                                         type="button"
                                                         onClick={(e) => {
                                                           e.stopPropagation();
                                                           setMappingPrefill({
                                                             symbol: s.symbol,
                                                             isin: s.isin,
                                                             resolved: t,
                                                             notes: `Picked from 404 retry triedTickers (chose: ${t}; all tried: ${tried.join(", ")})`,
                                                           });
                                                           setRetryReportOpen(false);
                                                         }}
                                                         className={`text-[10px] font-mono px-1.5 py-0.5 rounded border transition-colors cursor-pointer hover:bg-primary/10 hover:border-primary/40 ${
                                                           isBest
                                                             ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 font-semibold"
                                                             : had404
                                                             ? "bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300 line-through decoration-rose-500/50"
                                                             : "bg-muted border-border"
                                                         }`}
                                                       >
                                                         {isBest && "★ "}
                                                         {t}
                                                       </button>
                                                     </TooltipTrigger>
                                                     <TooltipContent side="top" className="text-[10px] space-y-0.5 p-2">
                                                       <div className="font-semibold">Map this ticker → {t}</div>
                                                       <div className="text-muted-foreground">
                                                         Score: {score} {isBest && "(best)"}
                                                       </div>
                                                       <div className="text-muted-foreground">
                                                         Attempts: {tAttempts.length}
                                                         {had404 && " · had 404"}
                                                       </div>
                                                     </TooltipContent>
                                                   </Tooltip>
                                                 </TooltipProvider>
                                               );
                                             })}
                                           </div>
                                         )}
                                      </div>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                              {isOpen && (
                                <TableRow className="bg-muted/30 hover:bg-muted/30">
                                  <TableCell colSpan={5} className="p-3 space-y-3">
                                    {(() => {
                                      const attempts: AttemptRecord[] = s.rawResponse?.attempts || [];
                                      if (attempts.length === 0) return null;
                                      const t0 = new Date(attempts[0].startedAt).getTime();
                                      const finalCls = classifyQuoteError(s.currentError);
                                      const toneStyles: Record<string, string> = {
                                        notFound: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
                                        rateLimit: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
                                        server: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
                                        network: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
                                        noPrice: "bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30",
                                        unknown: "bg-muted text-muted-foreground border-border",
                                      };
                                      return (
                                        <div className="space-y-2">
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                              Retry timeline ({attempts.length} attempt{attempts.length === 1 ? "" : "s"})
                                            </span>
                                            <Badge variant="outline" className={`text-[10px] ${toneStyles[finalCls.tone]}`}>
                                              Final: {finalCls.label}
                                            </Badge>
                                          </div>
                                          <ol className="relative border-l border-border pl-4 space-y-1.5">
                                            {attempts.map((a, idx) => {
                                              const dt = new Date(a.startedAt);
                                              const offsetMs = dt.getTime() - t0;
                                              return (
                                                <li key={idx} className="relative">
                                                  <span
                                                    className={`absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background ${
                                                      a.ok
                                                        ? "bg-emerald-500"
                                                        : a.status === 404
                                                        ? "bg-rose-500"
                                                        : a.status === 429
                                                        ? "bg-amber-500"
                                                        : a.status && a.status >= 500
                                                        ? "bg-orange-500"
                                                        : "bg-sky-500"
                                                    }`}
                                                  />
                                                  <div className="text-[11px] leading-snug">
                                                    <span className="text-muted-foreground font-mono">
                                                      +{(offsetMs / 1000).toFixed(2)}s
                                                    </span>{" "}
                                                    <span className="text-muted-foreground">
                                                      {dt.toLocaleTimeString(undefined, { hour12: false })}
                                                    </span>{" "}
                                                    <code className="font-mono px-1 py-0.5 rounded bg-muted border border-border">
                                                      {a.ticker}
                                                    </code>{" "}
                                                    <span className="text-muted-foreground">
                                                      attempt {a.attempt} · {a.durationMs}ms
                                                    </span>
                                                    {a.ok ? (
                                                      <Badge variant="outline" className="ml-1 text-[10px] bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                                                        OK
                                                      </Badge>
                                                    ) : (
                                                      <>
                                                        {a.status && (
                                                          <Badge variant="outline" className="ml-1 text-[10px] bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30">
                                                            {a.status}
                                                          </Badge>
                                                        )}
                                                        {a.error && (
                                                          <span className="ml-1 text-rose-700 dark:text-rose-300 break-all">
                                                            {a.error}
                                                          </span>
                                                        )}
                                                        {typeof a.delayMs === "number" && (
                                                          <span className="ml-1 text-muted-foreground">
                                                            → backoff {a.delayMs}ms
                                                          </span>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                </li>
                                              );
                                            })}
                                          </ol>
                                        </div>
                                      );
                                    })()}
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        Raw quote response
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2 text-xs"
                                        onClick={() =>
                                          navigator.clipboard.writeText(JSON.stringify(s.rawResponse, null, 2))
                                        }
                                      >
                                        Copy JSON
                                      </Button>
                                    </div>
                                    <pre className="text-[11px] font-mono bg-background border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64">
{JSON.stringify(s.rawResponse, null, 2)}
                                    </pre>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                );
              })()}

              {retryReport.missingFromResponse.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-500" /> Missing from response (
                    {retryReport.missingFromResponse.length})
                  </h4>
                  <div className="border rounded-md max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-8"></TableHead>
                          <TableHead>Symbol</TableHead>
                          <TableHead>ISIN</TableHead>
                          <TableHead>Previous error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {retryReport.missingFromResponse.map((m, i) => {
                          const rowKey = `mr-${m.symbol}|${m.isin}-${i}`;
                          const isOpen = !!expandedRows[rowKey];
                          const payload = {
                            symbol: m.symbol,
                            isin: m.isin,
                            note: "No entry returned by edge function for this symbol|isin pair.",
                            previousError: m.previousError,
                            invokeError: retryReport.invokeError ?? null,
                          };
                          return (
                            <Fragment key={rowKey}>
                              <TableRow
                                className="cursor-pointer hover:bg-muted/40"
                                onClick={() =>
                                  setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }))
                                }
                              >
                                <TableCell className="w-8 p-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    aria-label={isOpen ? "Hide quote response" : "View quote response"}
                                    aria-expanded={isOpen}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
                                    }}
                                  >
                                    {isOpen ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </Button>
                                </TableCell>
                                <TableCell className="font-mono">{m.symbol}</TableCell>
                                <TableCell className="font-mono text-xs">{m.isin || "—"}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="truncate">{m.previousError}</span>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-6 px-2 text-[10px] shrink-0"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMappingPrefill({ symbol: m.symbol, isin: m.isin });
                                        setRetryReportOpen(false);
                                      }}
                                    >
                                      <Link2 className="h-3 w-3 mr-1" /> Map
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                              {isOpen && (
                                <TableRow className="bg-muted/30 hover:bg-muted/30">
                                  <TableCell colSpan={4} className="p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        Raw quote response
                                      </span>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-2 text-xs"
                                        onClick={() =>
                                          navigator.clipboard.writeText(JSON.stringify(payload, null, 2))
                                        }
                                      >
                                        Copy JSON
                                      </Button>
                                    </div>
                                    <pre className="text-[11px] font-mono bg-background border rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64">
{JSON.stringify(payload, null, 2)}
                                    </pre>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {retryReport.recovered.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> Recovered ({retryReport.recovered.length})
                  </h4>
                  <div className="border rounded-md max-h-48 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Symbol</TableHead>
                          <TableHead>ISIN</TableHead>
                          <TableHead className="text-right">Price</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {retryReport.recovered.map((r, i) => (
                          <TableRow key={`rc-${i}`}>
                            <TableCell className="font-mono">{r.symbol}</TableCell>
                            <TableCell className="font-mono text-xs">{r.isin || "—"}</TableCell>
                            <TableCell className="text-right font-mono">{r.price}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {retryReport.recovered.length === 0 &&
                retryReport.stillFailing.length === 0 &&
                retryReport.missingFromResponse.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">No data in the last retry.</p>
                )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StockPriceGathering;
